import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import API from "../services/api";
import {
  applyThemePreference,
  normalizeThemePreference,
  resolveTheme,
  THEME_STORAGE_KEY
} from "../utils/theme";

const ThemeContext = createContext({
  preference: "system",
  resolved: "light",
  setPreference: () => {},
  refreshFromServer: async () => {}
});

export function ThemeProvider({ children }) {
  const location = useLocation();
  const [preference, setPreferenceState] = useState(() =>
    normalizeThemePreference(typeof localStorage !== "undefined" ? localStorage.getItem(THEME_STORAGE_KEY) : "system")
  );

  const resolved = useMemo(() => resolveTheme(preference), [preference]);

  const setPreference = useCallback((next) => {
    const normalized = normalizeThemePreference(next);
    setPreferenceState(normalized);
    applyThemePreference(normalized);
  }, []);

  useEffect(() => {
    applyThemePreference(preference);
  }, [preference]);

  useEffect(() => {
    if (preference !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyThemePreference("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get("/user/profile");
        const t = res.data?.theme_preference;
        if (cancelled || t == null) return;
        const normalized = normalizeThemePreference(t);
        setPreferenceState(normalized);
        applyThemePreference(normalized);
      } catch {
        /* offline or 401 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const refreshFromServer = useCallback(async () => {
    try {
      const res = await API.get("/user/profile");
      const t = res.data?.theme_preference;
      if (t == null) return;
      const normalized = normalizeThemePreference(t);
      setPreferenceState(normalized);
      applyThemePreference(normalized);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference, refreshFromServer }),
    [preference, resolved, setPreference, refreshFromServer]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
